import { Checkbox } from '@nike/eds'
import { ChangeEvent } from 'react'
import { MultilineSearchInput } from '../../../common/components/multiline-input/MultilineSearchInput'
import { shipStatusMapping } from '../../../common/utils/formatter'
import { InputGroup } from '../../../common/components/fields/InputGroup'
import { CarrierHubTreeDropdown } from '../../../common/components/treedropdown/CarrierHubTreeDropdown'
import { useCarrierHubList } from '../../../common/hooks/useCarrierHubList'
import { SinglelineSearchInput } from '../../../common/components/multiline-input/SinglelineSearchInput'
import { getSelectedShippingPoints, getShippingPoints } from '../ShippingPoints'
import {
  getDateValueInTimezone,
  getEndOfDayInTimezone,
  getStartOfDayInTimezone,
} from '../../../common/utils/DateUtils'
import { useCountriesList } from '../../../common/hooks/useCountriesList'
import { CountriesAutocomplete } from '../../../common/components/Autocomplete/CountriesAutocomplete'
import { isArray } from '@apollo/client/utilities'
import { ShippingPointsAutocomplete } from '../../../common/components/Autocomplete/ShippingPointsAutocomplete'
import { getOrderTypeCodes, getSelectedOrderTypeCodes } from '../OrderTypes'
import { OrderTypeCodesAutocomplete } from '../../../common/components/Autocomplete/OrderTypeCodesAutocomplete'

const SPLIT_REGEX = /[\s,;\t]+/
const statusOptions = [
  {
    value: 'CREATED',
    label: 'Created',
  },
  {
    value: 'CANCELLED',
    label: 'Cancelled',
  },
  {
    value: 'PARTIALLY_LOADED',
    label: 'Partially Loaded',
  },
  {
    value: 'PARTIALLY_DELIVERED',
    label: 'Partially Delivered',
  },
  {
    value: 'LOADED',
    label: 'Loaded',
  },
  {
    value: 'COMPLETELY_DELIVERED',
    label: 'Completely Delivered',
  },
  {
    value: 'SHIPPED',
    label: 'Shipped',
  },
]

export function ShipmentSearchInputs({ inputState, setInputState, selectedCarrierHubs }) {
  const { data: carrierHubData, isSuccess: isCarrierHubCallSuccess } = useCarrierHubList()
  const { data: countryData, isSuccess: isCountriesCallSuccess } = useCountriesList()

  function mapShipmentStatus(statusParameter: string) {
    if (!statusParameter) {
      return null
    }
    const statusArray = Array.isArray(statusParameter)
      ? statusParameter
      : statusParameter?.split(SPLIT_REGEX)
    return statusArray.map((s) => {
      return {
        value: s,
        label: shipStatusMapping[s],
      }
    })
  }

  function updateCheckboxParameter(event: ChangeEvent<HTMLInputElement>) {
    const newValue =
      inputState?.calculatedShipmentStatusCodes?.length > 0
        ? inputState?.calculatedShipmentStatusCodes?.split(',')
        : []
    event.target.checked
      ? newValue.push(event.target.value)
      : newValue?.splice(newValue.indexOf(event.target.id), 1)
    setInputState((prevState) => ({
      ...prevState,
      [event.target.name]: newValue.toString(),
    }))
  }

  function updateTTCheckboxParameter(event: ChangeEvent<HTMLInputElement>) {
    setInputState((prevState) => ({
      ...prevState,
      [event.target.name]: event.target.checked ? 'hasTTExceptions' : undefined,
    }))
  }

  function updateTextAreaSearchParameter(event: ChangeEvent<HTMLTextAreaElement>) {
    setInputState((prevState) => ({
      ...prevState,
      [event.target.name]: event.target.value,
    }))
  }

  function updateSelectedShippingPointCodes(_, value) {
    setInputState((prevState) => ({
      ...prevState,
      shippingPointCodes: value.map((o) => o.value).join(','),
    }))
  }

  function updateSelectedOrderTypeCodes(_, value) {
    setInputState((prevState) => ({
      ...prevState,
      orderTypeCodes: value.map((o) => o.value).join(','),
    }))
  }

  function updateActualShippedTimestampFrom(event: ChangeEvent<HTMLTextAreaElement>) {
    setInputState((prevState) => ({
      ...prevState,
      [event.target.name]: getStartOfDayInTimezone(event.target.value, 'Europe/Brussels'),
    }))
  }

  function updateActualShippedTimestampTo(event: ChangeEvent<HTMLTextAreaElement>) {
    setInputState((prevState) => ({
      ...prevState,
      [event.target.name]: getEndOfDayInTimezone(event.target.value, 'Europe/Brussels'),
    }))
  }

  function updateActualDeliveryDateFrom(event: ChangeEvent<HTMLTextAreaElement>) {
    setInputState((prevState) => ({
      ...prevState,
      [event.target.name]: getStartOfDayInTimezone(event.target.value, 'Europe/Brussels'),
    }))
  }

  function updateActualDeliveryDateTo(event: ChangeEvent<HTMLTextAreaElement>) {
    setInputState((prevState) => ({
      ...prevState,
      [event.target.name]: getEndOfDayInTimezone(event.target.value, 'Europe/Brussels'),
    }))
  }

  function updateCarrierHubs(selectedCarrierHubs: string) {
    setInputState((prevState) => ({
      ...prevState,
      carrierHubs: selectedCarrierHubs,
    }))
  }

  function updateSelectedCountries(_, value) {
    setInputState((prevState) => ({
      ...prevState,
      shipToCountryCodes: value.map((o) => o.code).join(','),
    }))
  }

  function getSelectedCountries() {
    if (inputState.shipToCountryCodes) {
      const selectedCountryCodes = isArray(inputState.shipToCountryCodes)
        ? inputState.shipToCountryCodes
        : inputState.shipToCountryCodes.split(',')
      return selectedCountryCodes.map((o) => {
        return { code: o }
      })
    }
    return []
  }

  return (
    <div
      className="flex-column content-center justify-items-start"
      style={{ gap: 'var(--eds-space-8)' }}
    >
      <InputGroup label="Packlists" clearable={false} withDivider={false}>
        <MultilineSearchInput
          name="packlistNumbers"
          label=""
          value={inputState.packlistNumbers}
          onChange={updateTextAreaSearchParameter}
        />
      </InputGroup>

      <InputGroup label="Sales Orders" clearable={false} withDivider={false}>
        <MultilineSearchInput
          name="salesOrderNumbers"
          label=""
          value={inputState.salesOrderNumbers}
          onChange={updateTextAreaSearchParameter}
        />
      </InputGroup>

      <InputGroup label="Delivery Notes" clearable={false} withDivider={false}>
        <MultilineSearchInput
          name="deliveryNoteNumbers"
          label=""
          value={inputState.deliveryNoteNumbers}
          onChange={updateTextAreaSearchParameter}
        />
      </InputGroup>

      <InputGroup label="Carton / Tracking Numbers" clearable={false} withDivider={false}>
        <MultilineSearchInput
          name="goodsHolderNumbers"
          label=""
          value={inputState.goodsHolderNumbers}
          onChange={updateTextAreaSearchParameter}
        />
      </InputGroup>

      <InputGroup label="CMRs" clearable={false} withDivider={false}>
        <MultilineSearchInput
          name="proNumbers"
          label=""
          value={inputState.proNumbers}
          onChange={updateTextAreaSearchParameter}
        />
      </InputGroup>

      <InputGroup label="Trailer Access Numbers" clearable={false} withDivider={false}>
        <MultilineSearchInput
          name="vendorAccessNumbers"
          label=""
          value={inputState.vendorAccessNumbers}
          onChange={updateTextAreaSearchParameter}
        />
      </InputGroup>

      <InputGroup label="Trailer Numbers" clearable={false} withDivider={false}>
        <MultilineSearchInput
          name="transportHolderNumbers"
          label=""
          value={inputState.transportHolderNumbers}
          onChange={updateTextAreaSearchParameter}
        />
      </InputGroup>

      <InputGroup label="Trailer License Plate Numbers" clearable={false} withDivider={false}>
        <MultilineSearchInput
          name="transportHolderLicensePlateNumbers"
          label=""
          value={inputState.transportHolderLicensePlateNumbers}
          onChange={updateTextAreaSearchParameter}
        />
      </InputGroup>

      <InputGroup label="Trailer ID Reference Numbers" clearable={false} withDivider={false}>
        <MultilineSearchInput
          name="transportHolderIdReferenceNumbers"
          label=""
          value={inputState.transportHolderIdReferenceNumbers}
          onChange={updateTextAreaSearchParameter}
        />
      </InputGroup>

      <InputGroup label="Packlist Status" clearable={false} withDivider={false}>
        <div className="flex-row">
          <div className="flex-column">
            {statusOptions
              .filter((a, i) => i % 2 === 0)
              .map((option) => (
                <Checkbox
                  name="calculatedShipmentStatusCodes"
                  className="eds-spacing--mt-4 eds-spacing--pr-16"
                  id={option.value}
                  label={option.label}
                  value={option.value}
                  onChange={updateCheckboxParameter}
                  checked={mapShipmentStatus(inputState.calculatedShipmentStatusCodes)?.some(
                    (o) => o.value === option.value
                  )}
                />
              ))}
          </div>

          <div className="flex-column">
            {statusOptions
              .filter((a, i) => i % 2 === 1)
              .map((option) => (
                <Checkbox
                  name="calculatedShipmentStatusCodes"
                  className="eds-spacing--mt-4"
                  id={option.value}
                  label={option.label}
                  value={option.value}
                  onChange={updateCheckboxParameter}
                  checked={mapShipmentStatus(inputState.calculatedShipmentStatusCodes)?.some(
                    (o) => o.value === option.value
                  )}
                />
              ))}
          </div>
        </div>
      </InputGroup>

      <InputGroup label="Carrier / Hub" clearable={false} withDivider={false}>
        <CarrierHubTreeDropdown
          carrierHubs={isCarrierHubCallSuccess && carrierHubData ? carrierHubData : []}
          selectedCarrierHubs={selectedCarrierHubs}
          showPartiallySelected={true}
          onChange={updateCarrierHubs}
        />
      </InputGroup>

      <InputGroup label="Ship To Number" clearable={false} withDivider={false}>
        <MultilineSearchInput
          name="shipToCodes"
          label=""
          value={inputState.shipToCodes}
          onChange={updateTextAreaSearchParameter}
        />
      </InputGroup>

      <InputGroup label="Ship To Name" clearable={false} withDivider={false}>
        <MultilineSearchInput
          name="shipToNames"
          label=""
          value={inputState.shipToNames}
          onChange={updateTextAreaSearchParameter}
        />
      </InputGroup>

      <InputGroup label="Ship To Country" clearable={false} withDivider={false}>
        <CountriesAutocomplete
          name={'countries'}
          countryData={isCountriesCallSuccess && countryData ? countryData : []}
          selectedCountries={getSelectedCountries()}
          onChange={updateSelectedCountries}
        />
      </InputGroup>

      <InputGroup label="Shipping Point" clearable={false} withDivider={false}>
        <ShippingPointsAutocomplete
          name={'shippingPoints'}
          shippingPointData={getShippingPoints()}
          selectedShippingPoints={getSelectedShippingPoints(inputState.shippingPointCodes)}
          onChange={updateSelectedShippingPointCodes}
        />
      </InputGroup>

      <InputGroup label="Order Type" clearable={false} withDivider={false}>
        <OrderTypeCodesAutocomplete
          name={'orderTypeCodes'}
          orderTypeCodesData={getOrderTypeCodes()}
          selectedOrderTypeCodes={getSelectedOrderTypeCodes(inputState.orderTypeCodes)}
          onChange={updateSelectedOrderTypeCodes}
        />
      </InputGroup>

      <InputGroup label="Ship Date" clearable={false} withDivider={false}>
        <div className="flex-row">
          <SinglelineSearchInput
            name="actualShippedTimestampFrom"
            label=""
            type={'date'}
            value={getDateValueInTimezone(
              inputState.actualShippedTimestampFrom,
              'Europe/Brussels',
              'yyyy-MM-dd'
            )}
            onChange={updateActualShippedTimestampFrom}
            placeholder={'From Date'}
          />
          <SinglelineSearchInput
            name="actualShippedTimestampTo"
            label=""
            type={'date'}
            value={getDateValueInTimezone(
              inputState.actualShippedTimestampTo,
              'Europe/Brussels',
              'yyyy-MM-dd'
            )}
            onChange={updateActualShippedTimestampTo}
            placeholder={'To Date'}
          />
        </div>
      </InputGroup>

      <InputGroup label="Actual Delivery Date" clearable={false} withDivider={false}>
        <div className="flex-row">
          <SinglelineSearchInput
            name="actualDeliveryDateFrom"
            label=""
            type={'date'}
            value={getDateValueInTimezone(
              inputState.actualDeliveryDateFrom,
              'Europe/Brussels',
              'yyyy-MM-dd'
            )}
            onChange={updateActualDeliveryDateFrom}
            placeholder={'From Date'}
          />
          <SinglelineSearchInput
            name="actualDeliveryDateTo"
            label=""
            type={'date'}
            value={getDateValueInTimezone(
              inputState.actualDeliveryDateTo,
              'Europe/Brussels',
              'yyyy-MM-dd'
            )}
            onChange={updateActualDeliveryDateTo}
            placeholder={'To Date'}
          />
        </div>
      </InputGroup>

      <InputGroup label="Has T&T Exceptions" clearable={false} withDivider={false}>
        <Checkbox
          name="hasTTExceptions"
          className="eds-spacing--mt-4 eds-spacing--pr-16"
          id={'hasTTExceptions'}
          label={''}
          value={inputState.hasTTExceptions}
          onChange={updateTTCheckboxParameter}
          checked={inputState.hasTTExceptions === 'hasTTExceptions'}
        />
      </InputGroup>
    </div>
  )
}
